/**
 * ── THE ANALYTICS DOWNLOAD: one CSV per card, delivered as a ZIP (VERA-260) ──────────
 *
 * The toolbar's `Download` button exports THE CURRENT VIEW — every card on the canvas,
 * under the page's filters, at the moment the button is pressed.
 *
 * ## Why one file per card rather than one sheet
 *
 * The cards have INCOMPATIBLE SHAPES, and that is not a tidiness observation — it is the
 * whole reason this module exists in this form:
 *
 *   a stat tile      one scalar                          1 row,  2 columns
 *   a time chart     a series per metric, per bucket      N rows, 1 + M columns
 *   a leaderboard    ranked rows with five measures       N rows, 6 columns
 *   a scan map       individual located events            N rows, 12 columns
 *   a choropleth     one row per country, four measures   N rows, 6 columns
 *
 * Flattening those into one sheet loses information in every direction: a time series
 * pivoted beside a scalar either repeats the scalar down every row (asserting it varies
 * over time, which it does not) or leaves it in a stray cell nothing labels. A container
 * carries every shape at its own natural width, and it needs no card-picker UI — which is
 * the second reason: the canvas layout IS the curation. A reader who does not want a
 * card's data removes the card.
 *
 * ⚠️ CSV ONLY. PDF is deliberately not here — see `VRG-507-Platform.Export.Pdf.Reports`.
 * A PDF is a REPORT (visual, for people, charts as rendered); a CSV is DATA. They are two
 * features that happen to share a button, and building the second one badly inside the
 * first is how a "download" becomes a screenshot of a table.
 *
 * ## 🚨 EVERY FILE STATES ITS PROVENANCE, AND THIS IS THE LOAD-BEARING PART
 *
 * A figure without its scope is a liability the moment it leaves this page. "505" pasted
 * into a deck is unfalsifiable: nobody downstream can tell whether it was every scan the
 * brand has ever had, or scans of four certificates in Italy in the last week. The page
 * itself answers that — the date chip, the applied-filter row, the currency on each money
 * card — and a file that drops the answer is strictly more dangerous than the screen it
 * came from, because it travels further and is quoted more confidently.
 *
 * So each CSV opens with a key/value block naming the card, the company, the reporting
 * currency, the exact window, EVERY active filter, and the generation time, then a blank
 * line, then the data. The blank line is what lets a reader (and `pandas.read_csv(...,
 * skiprows=N)`) find the table; ragged leading rows are read fine by every spreadsheet.
 *
 * A DRAWN AREA gets a sentence rather than a value, because a ring genuinely cannot be
 * expressed as a filter string a reader could check. Saying "a drawn map area is in
 * effect" is the honest form: it tells the reader the figures are narrowed by something
 * they must go back to the page to see. Silently omitting it would be the failure mode
 * this whole block exists to prevent.
 *
 * ## The data is FETCHED at export time, not scraped from what is rendered
 *
 * Three of the five card shapes fetch inside their own component (`ScansByTime2`,
 * `ScansByLocation`, `TopInfluencers`), so their data is not reachable from the page. The
 * alternatives were to plumb an export callback down through every card — an opt-in that
 * §42's 2-of-37 finding says a new card will forget — or to read the redux slots those
 * components write, which is worse: `state.analytics.statistics` is a SINGLE slot that two
 * chart cards race into, so an export reading it could hand one card's series to another
 * card's file under that card's title. Re-issuing the request from the filters is the only
 * version where each file provably contains the question its own header names.
 *
 * The cost is honest and stated: a card's file can differ from its pixels if the data
 * changed between render and click. That window is seconds and the file is stamped with
 * `Generated at`, which is exactly the disclosure that makes it checkable.
 */
import type {
  ICountryMetrics,
  IScanMapPoint,
  ITopInfluencers,
  TopInfluencerDimension,
} from '@repo/types'

import { createApiCall, analyticsCountryMetricsApi, analyticsTopInfluencersApi } from 'services'
import type {
  AnalyticsFilters,
  AnalyticsSeriesCountField,
  GetAnalyticsStatisticsData,
  GetAnalyticsTotalsCounts,
  GetAnalyticsTotalsData,
} from 'store/analytics/types'
import { encodeRing } from 'utils/geoRing'
import { downloadBlob, toCsvFile } from 'utils/csv'
import { buildZip, type ZipEntry } from 'utils/zip'

import { CERT_STATUS_OPTIONS, SCAN_STATUS_OPTIONS } from './analyticsFilterFields'
import {
  SCANS_BY_COUNTRY_CARD_ID,
  parseAnalyticsVariantCardId,
  type AnalyticsCardCategory,
} from './analyticsGrid'
import {
  analyticsCardDimension,
  analyticsCardRankKey,
  analyticsChartBucketParams,
  mapDimensionScanStatuses,
  type AnalyticsMapDimension,
} from './analyticsDisplay'
// The per-card bucket preference, read WITHOUT React — see `buildChartCsv` for why this
// module resolves one bucket per card id rather than one for the whole archive (VRG-512).
import { analyticsCardGranularityOverride } from './analyticsCardGranularity'

/* ────────────────────────────────────────────────────────────────────────────────────
   WHAT THE PAGE HANDS OVER
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * A card's identity, as the exporter needs it.
 *
 * Deliberately NOT `AnalyticsCardItem` — that type carries `render`, a React closure this
 * module must never call. Taking the three fields it actually reads keeps the dependency
 * one-directional and makes it obvious at the call site that nothing is being rendered.
 */
export interface AnalyticsExportCard {
  id: string
  title?: string
  category?: AnalyticsCardCategory
}

/** One ROI metric card's exportable figure, lifted off `useRoiCards`. */
export interface AnalyticsExportRoiCard {
  id: string
  title: string
  /**
   * The RAW figure, unformatted — no currency symbol, no thousands separators, no `%`.
   *
   * Set by `useRoiCards` alongside the display string, in the same object literal, so the
   * two cannot drift into describing different numbers. `null` where the card has no
   * figure (blocked, withheld, or awaiting input), which exports as an empty cell — never
   * as `0`, which would assert a measurement that was not made.
   */
  rawValue: string | null
  /** The card's own basis line — "12 of 240 scans", "median days". Context, not a number. */
  unit?: string
  /** Why there is no figure, when there is none. */
  note?: string
}

export interface AnalyticsExportContext {
  /** The ids currently on the canvas, in the active view's order. */
  cardIds: readonly string[]
  /** Every card the page can render, so an id can be resolved to a title. */
  cards: readonly AnalyticsExportCard[]
  filters: AnalyticsFilters
  companyId: string
  companyName: string
  /** `companies.reporting_currency`. Every money column is denominated in it. */
  reportingCurrency: string
  /** The KPI tiles' source. Already fetched by the page. */
  totals?: GetAnalyticsTotalsData
  /** The ROI tiles' figures, by card id. Already computed by the page. */
  roiCards: ReadonlyMap<string, AnalyticsExportRoiCard>
  /** The map cards' all/latest mode, which changes which points the endpoint returns. */
  scansMapMode: 'all' | 'latest'
  /** The leaderboard's grouping axis. Part of that card's identity, so part of its file. */
  topInfluencerDimension: TopInfluencerDimension
  /** Injectable for tests. Stamped into every file's header. */
  generatedAt?: Date
}

export interface AnalyticsExportResult {
  filename: string
  blob: Blob
  /**
   * How many CARD files the archive holds.
   *
   * ⚠️ EXCLUDES the `00-not-exported.csv` manifest, which is written only when something
   * was skipped. Counting it would make "Exported 5 files" true of an archive holding
   * four cards and an apology — the one arrangement in which the count is most likely to
   * be read as reassurance.
   */
  fileCount: number
  /**
   * Cards that produced no file, with the reason.
   *
   * Reported rather than swallowed: a zip that silently holds four files for five cards
   * looks complete, and the reader has no way to notice the missing one. The caller
   * surfaces these.
   */
  skipped: { title: string; reason: string }[]
}

/* ────────────────────────────────────────────────────────────────────────────────────
   THE PROVENANCE BLOCK
   ──────────────────────────────────────────────────────────────────────────────────── */

/** `'(all)'` rather than an empty cell: an unset filter and an unreadable one differ. */
const NOT_NARROWED = '(all)'

const labelFor = (
  options: readonly { value: string; label: string }[],
  value: string,
): string => options.find(o => o.value === value)?.label ?? value

/**
 * A multi-select filter as one cell.
 *
 * IDS ARE WRITTEN OUT IN FULL, not summarised as "3 selected". A count tells a reader the
 * figure is narrowed and leaves them unable to reproduce it; the ids are what make the
 * export checkable against the database. They are long, and a long cell in a header block
 * costs nothing — the alternative costs the file its auditability.
 *
 * ⚠️ Owner / product / certificate filters export as IDS, NOT NAMES, and that is a stated
 * limitation rather than an oversight. The names live in three different caches that only
 * `AnalyticsAppliedFilters` and the filter drawer subscribe to (react-query for products,
 * two redux slots for owners and certificates — see that component's header for why the
 * two are read differently). Resolving them here would mean either a second label
 * mechanism, which is the drift §44 exists to stop, or four more fetches on a click.
 * An id is unambiguous, which is the property that matters most in an audit trail.
 */
const describeIds = (ids: readonly string[]): string =>
  ids.length === 0 ? NOT_NARROWED : ids.join(' ')

const describeStatuses = (
  values: readonly string[],
  options: readonly { value: string; label: string }[],
): string => (values.length === 0 ? NOT_NARROWED : values.map(v => labelFor(options, v)).join(', '))

/**
 * The header block every file opens with.
 *
 * `cardScope` is the card's OWN narrowing on top of the page's — a map variant that pins
 * `scan_statuses: ['invalid']` because that is what the card IS, the leaderboard's
 * grouping axis, the map's all/latest mode. Without it two files under two different
 * titles could carry identical-looking headers and different numbers, which is precisely
 * the confusion the block exists to remove.
 */
const provenanceRows = (
  ctx: AnalyticsExportContext,
  cardTitle: string,
  cardType: string,
  cardScope: readonly [string, string][] = [],
): string[][] => {
  const { filters } = ctx
  const rows: string[][] = [
    ['Vera analytics export'],
    ['Card', cardTitle],
    ['Card type', cardType],
    ['Company', ctx.companyName],
    /*
      Stated ONCE, here, rather than as a `Currency` column beside every money figure.

      Every money figure in this export is denominated in this code and NOTHING is
      converted — a resale priced in another currency is excluded from the aggregate and
      counted separately, never rescaled (see `IRoiMetrics.reporting_currency`). So the
      currency is a property of the whole file, not of individual cells, and a per-cell
      column would imply the file could hold more than one. The values themselves are bare
      numbers in MAJOR UNITS, with no symbol and no grouping, so the column stays summable.
    */
    ['Reporting currency (all money figures)', ctx.reportingCurrency],
    ['Date range from', filters.dateRange.from.toISOString()],
    ['Date range to', filters.dateRange.to.toISOString()],
    ['Generated at', (ctx.generatedAt ?? new Date()).toISOString()],
    ['Filter: Owners', describeIds(filters.ownerIds)],
    ['Filter: Products', describeIds(filters.productIds)],
    ['Filter: Certificates', describeIds(filters.certificateIds)],
    [
      'Filter: Certificate status',
      describeStatuses(filters.certificateStatuses, CERT_STATUS_OPTIONS),
    ],
    ['Filter: Scan status', describeStatuses(filters.scanStatuses, SCAN_STATUS_OPTIONS)],
    ['Filter: Location', filters.location.length === 0 ? NOT_NARROWED : filters.location.join(', ')],
    /*
      The drawn area gets a SENTENCE, not a value.

      A ring is a polygon of coordinates. Printing it would be technically complete and
      practically unreadable, and printing nothing would leave a reader believing the
      figures cover everywhere. Naming that it is applied is the only form that is both
      honest and useful: it tells them the number is narrowed by something they have to
      return to the page to see. The encoded ring follows on its own row for anyone who
      does need to reproduce the query exactly.
    */
    [
      'Filter: Drawn map area',
      filters.area
        ? 'Applied — these figures cover only scans inside an area drawn on the map.'
        : NOT_NARROWED,
    ],
  ]

  if (filters.area) {
    rows.push(['Drawn area (lat,lng;…)', encodeRing(filters.area) ?? ''])
  }

  for (const [key, value] of cardScope) {
    rows.push([`This card: ${key}`, value])
  }

  // The blank row is the separator between provenance and data. See the module header.
  rows.push([])
  return rows
}

/* ────────────────────────────────────────────────────────────────────────────────────
   REQUEST PARAMS — built ONCE, from the filters, exactly as the cards' hooks build them
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The page filters in wire form.
 *
 * ⚠️ Mirrors what `useAnalyticsAreaParam` / `useAnalyticsScanStatusParam` inject inside
 * the hooks. It has to be re-derived here because those are HOOKS and this runs in a
 * click handler — but it is derived from the same `state.analytics.filters` the hooks
 * read, through the same `encodeRing`, so the two cannot disagree about what the page is
 * filtered to. Empty arrays are dropped rather than sent: `scan_statuses: []` means NO
 * NARROWING to the server, not "nothing", so sending one would silently widen the export
 * past what the card shows.
 */
const baseParams = (ctx: AnalyticsExportContext) => {
  const { filters } = ctx
  const area = encodeRing(filters.area)
  return {
    from: filters.dateRange.from.toISOString(),
    to: filters.dateRange.to.toISOString(),
    ...(filters.ownerIds.length ? { owner_ids: filters.ownerIds } : {}),
    ...(filters.productIds.length ? { product_ids: filters.productIds } : {}),
    ...(filters.certificateIds.length ? { certificate_ids: filters.certificateIds } : {}),
    ...(filters.certificateStatuses.length
      ? { certificate_statuses: filters.certificateStatuses }
      : {}),
    ...(filters.location.length ? { location: filters.location } : {}),
    ...(area ? { area } : {}),
  }
}

/** The page's scan-outcome filter, or `undefined`. Separated because pinned cards override it. */
const pageScanStatuses = (ctx: AnalyticsExportContext): string[] | undefined =>
  ctx.filters.scanStatuses.length ? [...ctx.filters.scanStatuses] : undefined

/* ────────────────────────────────────────────────────────────────────────────────────
   PER-SHAPE BUILDERS
   ──────────────────────────────────────────────────────────────────────────────────── */

/** `'scans_count'` → the label a chart column carries. Falls back to the raw field. */
const seriesColumnLabel = (metricId: string, cards: readonly AnalyticsExportCard[]): string =>
  cards.find(c => c.id === metricId)?.title ?? metricId

/**
 * A CARD id → the bucket field its series is read from.
 *
 * ⚠️ NOT `metricId` itself, and the difference is the bug this function exists to avoid.
 * A KPI tile's id already IS a bucket field (`scans_count`), but a ROI card's is not
 * (`mkt_invalid_scans`), and indexing a bucket with the latter yields `undefined` — an
 * empty column under a correct-looking header, which is the silent-wrong-answer failure
 * `toCsvRows` refuses for exactly the same reason.
 *
 * `analyticsCardDimension` is the ONE place that mapping lives (it reads the tile field
 * set and the ROI dimension table), so this composes with it rather than re-deriving it.
 * The `_count` suffix is how `AnalyticsSeriesCountField` and the dimension union relate —
 * `stripCount` is its inverse, on the same file.
 */
const seriesField = (metricId: string): AnalyticsSeriesCountField | null => {
  const dimension = analyticsCardDimension(metricId)
  return dimension ? (`${dimension}_count` as AnalyticsSeriesCountField) : null
}

/**
 * A METRIC TILE — one scalar, one row.
 *
 * KPI tiles read `totals` (a raw integer, exported as-is). ROI tiles read the `rawValue`
 * their own hook computed. Neither is parsed back out of a display string: `formatMoneyCents`
 * emits `$1,234.00`, and recovering `1234` from that means knowing the locale's grouping
 * character, which is a guess this module refuses to make.
 */
const buildMetricCsv = (
  ctx: AnalyticsExportContext,
  cardId: string,
  cardTitle: string,
): { rows: string[][] } | { skip: string } => {
  const totals = ctx.totals
  const totalsFields: Record<string, number | undefined> = {
    scans_count: totals?.scans_count,
    certificates_count: totals?.certificates_count,
    transfers_count: totals?.transfers_count,
    completed_transfers_count: totals?.completed_transfers_count,
    in_progress_transfers_count: totals?.in_progress_transfers_count,
  }

  if (cardId in totalsFields) {
    const value = totalsFields[cardId]
    if (value === undefined) return { skip: 'the totals for this period have not loaded' }
    const rows = [
      ...provenanceRows(ctx, cardTitle, 'Metric'),
      ['Metric', 'Value', 'Unit', 'Note'],
      [cardTitle, String(value), '', ''],
    ]
    /*
      The prior-period comparison, when the server measured one.

      Included because the tile SHOWS a delta, so a file omitting it would be missing a
      number the reader can see on screen. Its own window is stated rather than implied —
      `previous_range` is returned by the API precisely so a delta can be labelled with
      its basis instead of floating as a bare percentage.
    */
    const previous = totals?.previous?.[cardId as keyof GetAnalyticsTotalsCounts]
    if (typeof previous === 'number') {
      rows.push([
        `${cardTitle} (previous period)`,
        String(previous),
        '',
        totals?.previous_range
          ? `${totals.previous_range.from} to ${totals.previous_range.to}`
          : '',
      ])
    }
    return { rows }
  }

  const roi = ctx.roiCards.get(cardId)
  if (!roi) return { skip: 'this metric has no figure to export' }

  return {
    rows: [
      ...provenanceRows(ctx, cardTitle, 'Metric'),
      ['Metric', 'Value', 'Unit', 'Note'],
      [roi.title, roi.rawValue ?? '', roi.unit ?? '', roi.note ?? ''],
    ],
  }
}

/**
 * A CHART — one row per bucket, one column per plotted metric.
 *
 * ⚠️ THE COLUMNS ARE THE BUCKET TOTALS, not the per-certificate-status series the chart
 * splits into when a status filter is applied. That split is a rendering of the same
 * measurement (`scans_detailed` breaks one bucket's total down by status), and the totals
 * are what the card's own axis reads. A file carrying both would double-count on any
 * naive `SUM`, which is the specific way a spreadsheet export misleads. The active status
 * filter is in the header block, so the narrowing is stated even though the breakdown is
 * not enumerated.
 */
const buildChartCsv = async (
  ctx: AnalyticsExportContext,
  cardId: string,
  metricIds: readonly string[],
  cardTitle: string,
): Promise<{ rows: string[][] } | { skip: string }> => {
  const scanStatuses = pageScanStatuses(ctx)
  /*
    THE SAME BUCKET GRANULARITY THE CHART ASKED FOR.

    ⚠️ Not the server's default. `ScansByTime2` steps the bucket by range length — hour,
    day, 7-day, month — and an export that omitted the parameters would return HOURLY rows
    (the server's default granularity) under a chart drawn in months. The rows would still
    sum to the same total, so the file would look right — which is why this is worth
    reproducing rather than leaving to a default. The thresholds are
    `analyticsChartGranularity`, which that component reads too.

    ⚠️ SPREAD, NOT ONE FIELD. The bucket is a (count, granularity) PAIR since VRG-512 — a
    week is `7 day` — so sending only the granularity would turn a weekly chart's export
    into a daily one, which is the same silent mismatch in a new place.
  */
  /*
    🚨 THIS CARD'S CHOSEN BUCKET — RESOLVED PER `cardId`, NOT ONCE FOR THE WHOLE ZIP.

    This is the half of VRG-512 that fails silently if it is skipped, so it gets its own
    warning rather than sharing the one above.

    `buildAnalyticsExport` is a WHOLE-PAGE export: it iterates `ctx.cardIds` and writes one
    CSV per card. The "View by" preference used to be a single page-level field
    (`ctx.filters.granularity`), so ONE lookup out here was correct — every chart on the
    canvas was drawn at that unit. It is now per card (`analyticsCardGranularity`), and a
    single lookup would hand every chart file in the archive whichever card's bucket
    happened to be read: a Scans chart drawn in days would export monthly rows because the
    Certificates chart beside it was set to months.

    ⚠️ AND THE READER COULD NOT TELL. The monthly rows still sum to exactly the same totals
    as the daily ones, so the file opens, parses, and reconciles against the figure on the
    card — it simply answers a different question from the chart it is named after. That is
    the same failure the block above exists to prevent, one axis over, and it is why the
    bucket is resolved from the card id here rather than from `ctx.filters`.

    `analyticsCardGranularityOverride` is the plain non-React read of the same store the
    chart's `useAnalyticsCardGranularity` subscribes to — one source, two access shapes —
    and `analyticsChartBucketParams` then resolves it against the range exactly as the chart
    does, so a file exported while a card is drawn in months holds monthly rows for THAT
    card. Calling the range-only `analyticsChartGranularity` instead would drop the reader's
    choice entirely, which is the same mismatch again.
  */
  const chartBucket = analyticsChartBucketParams(
    ctx.filters.dateRange.from,
    ctx.filters.dateRange.to,
    analyticsCardGranularityOverride(cardId),
  )
  /*
    The bucket named for a human, for the column header below.

    A count of 1 reads as the bare unit ("one row per day"); anything else has to carry the
    count ("one row per 7 days"), because a weekly export whose header said "per day" would
    describe rows seven times smaller than the ones in the file — and since the rows still
    sum to the same total, nothing downstream would look wrong.
  */
  const bucketLabel =
    chartBucket.interval_count === 1
      ? chartBucket.interval_granularity
      : `${chartBucket.interval_count} ${chartBucket.interval_granularity}s`

  const { data } = await createApiCall({
    method: 'GET',
    url: '/analytics/admin/granular',
    params: {
      ...baseParams(ctx),
      ...chartBucket,
      company_ids: [ctx.companyId],
      ...(scanStatuses ? { scan_statuses: scanStatuses } : {}),
    },
  })

  const buckets: GetAnalyticsStatisticsData[] = data?.data ?? []
  if (buckets.length === 0) return { skip: 'no buckets in this period' }

  const columns = metricIds
    .map(id => ({ field: seriesField(id), label: seriesColumnLabel(id, ctx.cards) }))
    .filter((c): c is { field: AnalyticsSeriesCountField; label: string } => c.field !== null)

  if (columns.length === 0) return { skip: 'no plottable series resolved for this card' }

  return {
    rows: [
      ...provenanceRows(ctx, cardTitle, 'Chart'),
      [`Bucket start (UTC, one row per ${bucketLabel})`, ...columns.map(c => c.label)],
      ...buckets.map(bucket => [
        bucket.timestamp,
        ...columns.map(c => {
          const value = bucket[c.field]
          return typeof value === 'number' ? String(value) : ''
        }),
      ]),
    ],
  }
}

/**
 * A SCAN MAP — one row per located scan event.
 *
 * `mode` and the card's pinned outcome are both reproduced here, and both go in the
 * header block, because they change WHICH ROWS COME BACK. `latest` returns one point per
 * certificate rather than every scan, so the same filters under the two modes produce
 * different row counts for the same card title — a file that did not say which it was
 * would be unreconcilable against the screen.
 */
const buildMapCsv = async (
  ctx: AnalyticsExportContext,
  metricId: string,
  cardTitle: string,
): Promise<{ rows: string[][] } | { skip: string }> => {
  // The SHARED resolver, not a local `_count` strip — a ROI card's id is not its
  // dimension (`mkt_invalid_scans` -> `invalid_scans`), so a string operation here would
  // pin the wrong outcome on exactly the cards that have one.
  const dimension = analyticsCardDimension(metricId)
  if (!dimension) return { skip: 'this card has no map dimension' }

  const isCertificates = dimension === 'certificates'
  const mode = isCertificates ? 'latest' : ctx.scansMapMode

  /*
    THE SAME INTERSECTION `ScansByLocation` COMPUTES, for the same reason.

    A pinned card ("Invalid scans by location") asks for its own outcome because that is
    what it IS; the page filter may only narrow it further. Letting either win outright
    gives a card that plots a different measurement from its title, or a card that ignores
    a filter the toolbar says is applied. An EMPTY intersection cannot be sent — the server
    reads `scan_statuses: []` as no narrowing, i.e. every scan — so it is skipped instead.
  */
  const pinned = mapDimensionScanStatuses(dimension as AnalyticsMapDimension)
  const pageStatuses = ctx.filters.scanStatuses
  let effective: string[] | undefined
  if (pinned) {
    effective = pageStatuses.length ? pinned.filter(s => pageStatuses.includes(s)) : [...pinned]
    if (effective.length === 0) {
      return { skip: 'the page scan-status filter excludes everything this card plots' }
    }
  } else {
    effective = pageScanStatuses(ctx)
  }

  /*
    NO `company_ids`, matching `ScansByLocation`'s own payload exactly.

    Every other analytics request on this page names the company; the scans-map one does
    not, and is scoped server-side instead. Adding it here would be harmless in practice —
    it is the same company — but the point of re-issuing the card's request is that it IS
    the card's request, and a parameter the card never sends is one more way the file and
    the pixels could come to differ.
  */
  const { data } = await createApiCall({
    method: 'GET',
    url: '/analytics/admin/scans-map',
    params: {
      ...baseParams(ctx),
      mode,
      ...(effective && effective.length ? { scan_statuses: effective } : {}),
    },
  })

  const points: IScanMapPoint[] = data?.data ?? []

  const scope: [string, string][] = [
    ['Map mode', mode === 'latest' ? 'Latest location (one point per certificate)' : 'All scans'],
  ]
  if (pinned) scope.push(['Pinned scan outcome', pinned.join(', ')])

  return {
    rows: [
      ...provenanceRows(ctx, cardTitle, 'Map', scope),
      [
        'Scanned at (UTC)',
        'Scan ID',
        'Certificate ID',
        'Certificate status',
        'Product',
        'Latitude',
        'Longitude',
        'City',
        'State',
        'Country',
        'Verified',
        'Invalid',
        'Tampered',
      ],
      ...points.map(p => [
        p.created_at,
        p.scan_id,
        p.certificate_id,
        p.certificate_status,
        p.product_title,
        // Coordinates as raw decimals — `String` on a number never groups, which is what
        // keeps them re-importable as numbers rather than as text.
        String(p.geo_lat),
        String(p.geo_lng),
        p.geo_city ?? '',
        p.geo_state ?? '',
        p.geo_country ?? '',
        String(p.verified),
        String(p.invalid),
        p.tampered === null ? '' : String(p.tampered),
      ]),
    ],
  }
}

/** A LEADERBOARD — the ranked rows the card shows, plus the axis it is ranked on. */
const buildTableCsv = async (
  ctx: AnalyticsExportContext,
  baseId: string,
  cardTitle: string,
): Promise<{ rows: string[][] } | { skip: string }> => {
  const scanStatuses = pageScanStatuses(ctx)
  /*
    ⚠️ THE RANKING KEY IS PART OF THE REQUEST, from the same registry the card reads
    (VERA-260). Omit it and the file is ranked by scans while the card it was exported
    from is ranked by leads — the two disagree, silently, and the CSV is the copy that
    outlives the screen and gets forwarded. `rows` is truncated on this key, so it is not
    a re-ordering: the wrong key returns different ROWS.
  */
  const rankBy = analyticsCardRankKey(baseId)
  const result: ITopInfluencers = await analyticsTopInfluencersApi.get({
    ...baseParams(ctx),
    dimension: ctx.topInfluencerDimension,
    rank_by: rankBy,
    ...(scanStatuses ? { scan_statuses: scanStatuses } : {}),
  })

  /*
    TWO ROWS, because there are two axes and this header used to conflate them: "Ranked
    by" carried the DIMENSION, which is what the rows are GROUPED into, not what they are
    ordered on. That was merely imprecise while every export was scans-ranked; with a
    second ranking it would be wrong.

    `rank_by` is read from the RESPONSE rather than from the request, so the file records
    what the server actually did — the same reason `ITopInfluencers` echoes it.
  */
  const scope: [string, string][] = [
    ['Grouped by', ctx.topInfluencerDimension],
    ['Ranked by', result.rank_by],
  ]
  /*
    `unattributed_scans` is carried into the header rather than dropped.

    It is the count of scans that matched every filter and could not be ranked — an owner
    the certificate does not have, a scan with no resolved place. The endpoint returns it
    precisely so a leaderboard is not read as a complete account of the period's scans, and
    a file that omits it recreates exactly that misreading one step further from the page.
  */
  scope.push(['Unranked scans (no value on this axis)', String(result.unattributed_scans)])

  return {
    rows: [
      ...provenanceRows(ctx, cardTitle, 'Table', scope),
      ['Rank', 'Name', 'Detail', 'Scans', 'Certificates', 'Leads', 'Last scan (UTC)'],
      ...result.rows.map((row, i) => [
        String(i + 1),
        row.label,
        row.sublabel ?? '',
        String(row.scans),
        String(row.certificates),
        String(row.leads),
        row.last_scan_at,
      ]),
    ],
  }
}

/** THE CHOROPLETH — one row per country, carrying every measure the card can colour by. */
const buildCountryCsv = async (
  ctx: AnalyticsExportContext,
  cardTitle: string,
): Promise<{ rows: string[][] } | { skip: string }> => {
  const scanStatuses = pageScanStatuses(ctx)
  const result: ICountryMetrics = await analyticsCountryMetricsApi.get({
    ...baseParams(ctx),
    ...(scanStatuses ? { scan_statuses: scanStatuses } : {}),
  })

  const scope: [string, string][] = [
    /*
      The rate columns are suppressed ON SCREEN below this denominator, because a rate over
      two scans is arithmetically correct and statistically meaningless. The threshold is
      part of what the card shows, so it is part of what the file has to say — otherwise a
      reader computing `invalid_scans / scans` themselves gets a different answer from the
      map and has no way to find out why.
    */
    ['Minimum denominator for rates', String(result.min_denominator)],
    ['Scans with no resolved country', String(result.unresolved?.scans ?? 0)],
  ]

  return {
    rows: [
      ...provenanceRows(ctx, cardTitle, 'Map', scope),
      ['Country code', 'Country', 'Scans', 'Certificates', 'Invalid scans', 'Repeat certificates'],
      ...result.rows.map(row => [
        row.code,
        row.name,
        String(row.scans),
        String(row.certificates),
        String(row.invalid_scans),
        String(row.repeat_certificates),
      ]),
    ],
  }
}

/* ────────────────────────────────────────────────────────────────────────────────────
   ASSEMBLY
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * A filename-safe slug.
 *
 * ASCII-only, because the entry name lands in a ZIP header and, while bit 11 declares it
 * UTF-8 (see `utils/zip.ts`), Windows Explorer's built-in extractor has historically
 * ignored that flag. A slug is a label on a file the reader already has the header block
 * to identify — it is not carrying meaning that can be lost.
 */
const slug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'card'

/**
 * Build the archive.
 *
 * Cards are fetched CONCURRENTLY (`Promise.all`) — they are independent GETs and doing
 * them in series would make a six-card export six round-trips long for no benefit. A
 * card that throws is recorded in `skipped` rather than failing the whole export: one
 * endpoint erroring should not cost the reader the other five files, and the skip list is
 * what stops the shortfall being silent.
 */
export const buildAnalyticsExport = async (
  ctx: AnalyticsExportContext,
): Promise<AnalyticsExportResult> => {
  const generatedAt = ctx.generatedAt ?? new Date()
  const context: AnalyticsExportContext = { ...ctx, generatedAt }

  /*
    An empty canvas is refused rather than exported.

    A zero-entry zip is a VALID archive, so it downloads, opens, and contains nothing —
    and the success toast beside it would read "Exported 0 files", which describes the
    outcome accurately and still leaves the reader holding a file they cannot use. The
    export's subject is the view; a view with no cards has no subject.
  */
  if (ctx.cardIds.length === 0) {
    throw new Error('There are no cards on the canvas to export.')
  }

  const cardById = new Map(ctx.cards.map(c => [c.id, c]))

  const built = await Promise.all(
    ctx.cardIds.map(async (cardId, index) => {
      const card = cardById.get(cardId)
      const parsed = parseAnalyticsVariantCardId(cardId)
      const title = card?.title ?? cardId

      try {
        let outcome: { rows: string[][] } | { skip: string }
        let kind: string

        if (cardId === SCANS_BY_COUNTRY_CARD_ID) {
          kind = 'map'
          outcome = await buildCountryCsv(context, title)
        } else if (!parsed) {
          kind = 'metric'
          outcome = buildMetricCsv(context, cardId, title)
        } else if (parsed.display === 'chart') {
          kind = 'chart'
          // `cardId` — the bucket is that card's own since VRG-512; see `buildChartCsv`.
          outcome = await buildChartCsv(context, cardId, parsed.metricIds, title)
        } else if (parsed.display === 'map') {
          kind = 'map'
          /*
            ⚠️ THE GROUPING DECIDES THE EXPORT (VERA-260). A map card is pins OR a
            choropleth — two endpoints, two row shapes — so exporting a country-grouped
            card through `buildMapCsv` would hand the reader a file of individual scan
            points under a heading that says "per country", which is a different
            measurement wearing the card's title. The `scans_by_country` branch above stays
            for the retired standalone id; this is the same routing for the ids that
            replaced it.
          */
          outcome =
            parsed.grouping === 'country'
              ? await buildCountryCsv(context, title)
              : await buildMapCsv(context, parsed.baseId, title)
        } else if (parsed.display === 'table') {
          kind = 'table'
          outcome = await buildTableCsv(context, parsed.baseId, title)
        } else {
          kind = 'metric'
          outcome = buildMetricCsv(context, parsed.baseId, title)
        }

        if ('skip' in outcome) return { title, skip: outcome.skip }

        return {
          title,
          entry: {
            // The index prefix keeps the archive in the view's own order and guarantees
            // uniqueness: a metric tile and its chart legitimately share a title (see
            // `analyticsDisplayVariants`), so title alone would collide.
            name: `${String(index + 1).padStart(2, '0')}-${slug(title)}-${kind}.csv`,
            rows: outcome.rows,
          },
        }
      } catch (error) {
        return {
          title,
          skip: error instanceof Error ? error.message : 'the request for this card failed',
        }
      }
    }),
  )

  const encoder = new TextEncoder()
  const entries: ZipEntry[] = []
  const skipped: { title: string; reason: string }[] = []

  for (const result of built) {
    if ('entry' in result && result.entry) {
      entries.push({
        name: result.entry.name,
        data: encoder.encode(toCsvFile(result.entry.rows)),
      })
    } else if ('skip' in result && result.skip) {
      skipped.push({ title: result.title, reason: result.skip })
    }
  }

  /*
    A MANIFEST, but only when something was skipped.

    An unconditional one would be a file in every archive restating what the per-file
    headers already say, which is how a manifest becomes the thing nobody opens. A
    shortfall is different: it is the one fact NO other file in the archive can carry,
    because the file that would have carried it is the one that is missing.
  */
  // Taken BEFORE the manifest is appended — see `AnalyticsExportResult.fileCount`.
  const cardFileCount = entries.length

  if (skipped.length > 0) {
    const rows: string[][] = [
      ['Vera analytics export — cards with no file'],
      ['Generated at', generatedAt.toISOString()],
      [],
      ['Card', 'Why there is no file'],
      ...skipped.map(s => [s.title, s.reason]),
    ]
    entries.push({ name: '00-not-exported.csv', data: encoder.encode(toCsvFile(rows)) })
  }

  const stamp = generatedAt.toISOString().slice(0, 10)
  return {
    filename: `vera-analytics-${stamp}.zip`,
    blob: buildZip(entries, generatedAt),
    fileCount: cardFileCount,
    skipped,
  }
}

/** Build the archive and hand it to the browser. Returns the result so a caller can report it. */
export const downloadAnalyticsExport = async (
  ctx: AnalyticsExportContext,
): Promise<AnalyticsExportResult> => {
  const result = await buildAnalyticsExport(ctx)
  downloadBlob(result.blob, result.filename)
  return result
}
